import {
  useRef,
  useState,
  useEffect,
  useCallback,
  RefObject,
  MutableRefObject,
} from "react";

type ScrollDirection = "left" | "right" | null;

type UseEdgeScrollParams = {
  /** The ref of the element that has the scrollbar */
  scrollContainerRef:
    | RefObject<HTMLDivElement>
    | MutableRefObject<HTMLDivElement | undefined>;
  /** Initiates scrolling when mouse position is less than this value relative to the scroll container */
  leftScrollAt: number;
  /** Same as left, but relative to the right edge. So 0 would be the right edge of the scroll container */
  rightScrollAt: number;
  /** Max scroll speed, roughly how many pixels to scroll per millisecond */
  scrollSpeed?: number;
};

type UseEdgeScrollReturn = {
  runEdgeScroller: (event: MouseEvent | React.DragEvent) => void;
  stopEdgeScroller: () => void;
};

/**
 * Used within CpDataTable to allow for easier reordering of columns by extending
 * the left/right edge of the drag scroll trigger.
 *
 * This hook enables automatic scrolling when the mouse approaches the edges of a
 * scrollable container, which is useful for drag-and-drop interactions where users
 * need to scroll while dragging.
 *
 * @param params - Configuration object for edge scrolling behavior
 * @returns Object containing runEdgeScroller and stopEdgeScroller functions
 *
 * @example
 * ```tsx
 * const { runEdgeScroller, stopEdgeScroller } = useEdgeScroll({
 *   scrollContainerRef: tableContainerRef,
 *   leftScrollAt: 280,
 *   rightScrollAt: 90,
 *   scrollSpeed: 2,
 * });
 * ```
 */
export function useEdgeScroll({
  scrollContainerRef,
  leftScrollAt,
  rightScrollAt,
  scrollSpeed = 1,
}: UseEdgeScrollParams): UseEdgeScrollReturn {
  const [scrolling, setScrolling] = useState<ScrollDirection>(null);
  const speedScale = useRef(0); // Used to increase speed as you approach edge
  const prevTime = useRef<number | null>(null);
  const loopRequestId = useRef<number>();

  useEffect(() => {
    if (scrolling) {
      function step(timestamp: number) {
        if (prevTime.current === null) {
          prevTime.current = timestamp;
        }

        // The amount of time passed since last frame in ms
        const deltaTime = timestamp - prevTime.current;
        prevTime.current = timestamp;

        // We make sure to apply the scroll in relation to the delta time in order to have consistent velocities with differing refresh rates and rendering performances
        // e.g. A 120hz display would run this twice as much compared to 60hz resulting in a much faster scroll speed
        if (deltaTime > 0 && scrollContainerRef.current) {
          const speed = scrollSpeed * speedScale.current;
          if (scrolling === "left") {
            scrollContainerRef.current.scrollLeft -= speed * deltaTime;
          } else if (scrolling === "right") {
            scrollContainerRef.current.scrollLeft += speed * deltaTime;
          }
        }

        loopRequestId.current = window.requestAnimationFrame(step);
      }

      window.requestAnimationFrame(step);
      return () => {
        if (loopRequestId.current) {
          window.cancelAnimationFrame(loopRequestId.current);
        }
      };
    }
  }, [scrolling, scrollSpeed, scrollContainerRef]);

  const runEdgeScroller = useCallback(
    (event: MouseEvent | React.DragEvent) => {
      if (event.clientX <= 0 || !scrollContainerRef.current) return;

      const rect = scrollContainerRef.current.getBoundingClientRect();
      const relativeLeftX = event.clientX - rect.left;
      const relativeRightX = rect.right - event.clientX;

      if (relativeLeftX <= leftScrollAt) {
        setScrolling("left");
        speedScale.current = 1 - relativeLeftX / leftScrollAt;
      } else if (relativeRightX <= rightScrollAt) {
        setScrolling("right");
        speedScale.current = 1 - relativeRightX / rightScrollAt;
      } else {
        setScrolling(null);
      }
    },
    [leftScrollAt, rightScrollAt, scrollContainerRef],
  );

  const stopEdgeScroller = useCallback(() => setScrolling(null), []);

  return {
    runEdgeScroller,
    stopEdgeScroller,
  };
}
